import { PurchaseActionOption, PurchaseItemStatus } from "common";
import React, { useEffect, useState } from "react";
import { Purchase, PurchaseItem } from "types";
import { usePurchase } from "../../../hooks/resources/usePurchase";
import { useUpdateEffect } from "../../../hooks/useUpdateEffect";
import { CollectPurchaseBalanceModal } from "../../PurchaseModification/CollectPurchaseBalanceModal";
import { BalanceBadge } from "./BalanceBadge";
import {
    CollectBalanceOption,
    CollectBalanceOptionsModal,
} from "@/components/PurchaseModification/CollectBalanceDeciderModal";
import { CollectPurchaseBalanceViaInvoiceModal } from "@/components/PurchaseModification/CollectBalanceViaInvoiceModal";
import { isInvoiceEnabled } from "@/stores/globalStore";

interface PurchaseBalanceBadgeProps {
    purchase: Purchase;
    item: PurchaseItem;
    size?: string;
    className?: string;
    onOpenChange?: (isOpen: boolean) => void;
}

export const PurchaseBalanceBadge = ({
    purchase,
    item,
    size = "small",
    className,
    onOpenChange,
}: PurchaseBalanceBadgeProps) => {
    const [isSelectionOptionModalOpened, setIsSelectionOptionModalOpened] = useState(false);

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsSelectionOptionModalOpened(true);
    };

    if (!purchase || purchase.balance === 0) {
        return null;
    }

    return (
        <>
            <BalanceBadge value={purchase.balance} size={size} className={className} onClick={handleClick} />
            {isSelectionOptionModalOpened && (
                <CollectBalance
                    purchase={purchase}
                    item={item}
                    onOpenChange={onOpenChange}
                    onClose={() => setIsSelectionOptionModalOpened(false)}
                />
            )}
        </>
    );
};

interface CollectBalanceProps {
    purchase: Purchase;
    item: PurchaseItem;
    onClose: () => void;
    onOpenChange?: (isOpen: boolean) => void;
}

const CollectBalance = ({ purchase: initialPurchase, item, onClose, onOpenChange }: CollectBalanceProps) => {
    const { purchase, isLoadingPurchase } = usePurchase(initialPurchase.id);
    const [isSelectionOptionModalOpened, setIsSelectionOptionModalOpened] = useState(false);
    const [selectedOption, setSelectedOption] = useState<PurchaseActionOption | null>(null);

    useUpdateEffect(() => {
        onOpenChange?.(isSelectionOptionModalOpened);
    }, [isSelectionOptionModalOpened]);

    useEffect(() => {
        if (!purchase) {
            return;
        }

        const showInvoiceOption = isInvoiceEnabled() && purchase.customerEmail;
        const isSomeItemConfirmed = purchase.items.some((item) => item.status === PurchaseItemStatus.CONFIRMED);
        if (isSomeItemConfirmed) {
            if (showInvoiceOption) {
                setIsSelectionOptionModalOpened(true);
            } else {
                setSelectedOption(PurchaseActionOption.COLLECT_BALANCE);
            }
        }
    }, [purchase]);

    if (isLoadingPurchase || !purchase) {
        return null;
    }

    const handleModalClose = () => {
        setSelectedOption(null);
        setIsSelectionOptionModalOpened(false);
        onClose();
    };

    return (
        <>
            <CollectBalanceOptionsModal
                isOpen={isSelectionOptionModalOpened}
                purchase={purchase}
                showPayLater={false}
                onClose={handleModalClose}
                onConfirm={(action: CollectBalanceOption) => {
                    if (action === CollectBalanceOption.COLLECT_NOW) {
                        setSelectedOption(PurchaseActionOption.COLLECT_BALANCE);
                    } else if (action === CollectBalanceOption.COLLECT_VIA_INVOICE) {
                        setSelectedOption(PurchaseActionOption.COLLECT_BALANCE_VIA_INVOICE);
                    } else {
                        setSelectedOption(null);
                    }
                }}
            />

            <CollectPurchaseBalanceViaInvoiceModal
                purchase={purchase}
                item={item}
                isOpen={selectedOption === PurchaseActionOption.COLLECT_BALANCE_VIA_INVOICE}
                onClose={handleModalClose}
            />

            <CollectPurchaseBalanceModal
                isOpen={selectedOption === PurchaseActionOption.COLLECT_BALANCE}
                purchase={purchase}
                onClose={handleModalClose}
            />
        </>
    );
};
